import React, { useEffect, useState } from "react"
import { User } from "../../../../types/UserData"
import { TACreateApplication } from "../../../../services/applicationAPI"
import { getErrorStatus, getErrorMessage } from "../../../../types/error"
import { useNavigate } from "react-router-dom"

interface InviteUsersModalProps {
  isOpen: boolean
  onClose: () => void
  selectedUsers: User[]
}

interface UserPrice {
  userId: string
  price_user: number
  price_brand: number
}

const CAMPAIGN_TYPES = ["insta-post", "insta-story", "insta-reels", "tiktok", "ugc"] as const

const InviteUsersModal: React.FC<InviteUsersModalProps> = ({ isOpen, onClose, selectedUsers }) => {
  const navigate = useNavigate()
  const [campaignId, setCampaignId] = useState<string>("")
  const [campaignType, setCampaignType] = useState<string>("insta-post")
  const [userPrices, setUserPrices] = useState<UserPrice[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>("")
  const [success, setSuccess] = useState<string>("")

  useEffect(() => {
    if (isOpen) {
      const initialPrices = selectedUsers.map((user) => ({
        userId: user._id,
        price_user: 0,
        price_brand: 0,
      }))
      setUserPrices(initialPrices)
      // Reset form when modal opens
      setCampaignId("")
      setCampaignType("insta-post")
      setError("")
      setSuccess("")
    }
  }, [isOpen, selectedUsers])

  const handlePriceChange = (
    userId: string,
    field: "price_user" | "price_brand",
    value: number,
  ) => {
    setUserPrices((prev) =>
      prev.map((up) => (up.userId === userId ? { ...up, [field]: value } : up)),
    )
  }

  const handleBulkPriceChange = (field: "price_user" | "price_brand", value: number) => {
    setUserPrices((prev) => prev.map((up) => ({ ...up, [field]: value })))
  }

  const handleSubmit = async () => {
    if (!campaignId || campaignId.trim() === "") {
      setError("Lütfen bir kampanya _id girin")
      return
    }

    if (userPrices.some((up) => up.price_user <= 0 || up.price_brand <= 0)) {
      setError("Tüm kullanıcılar için price_user ve price_brand değerleri 0'dan büyük olmalıdır")
      return
    }

    setError("")
    setSuccess("")
    setIsLoading(true)

    try {
      const results: string[] = []
      const errors: { userId: string; userName: string; error: string }[] = []

      for (const user of selectedUsers) {
        const userPrice = userPrices.find((up) => up.userId === user._id)
        if (!userPrice) {
          continue
        }

        try {
          const applicationUser = {
            user_id: user._id,
            insta_username: user.insta?.username || "",
            age: user.age,
            gender: user.gender,
            verification: user.verification,
            state: "invite_new",
            school_type: user.school_type,
            school_name: user.school_name,
            city: user.city,
            country: user.country,
            language: user.language,
            currency: user.currency,
            job: user.job,
            birthday: user.birthday,
            price_user: userPrice.price_user,
            price_brand: userPrice.price_brand,
            tiktok_username: user.tiktok?.username || "",
            insta: user.insta || {},
            tiktok: user.tiktok || {},
          }

          await TACreateApplication(applicationUser, campaignId, "invite_new", campaignType)
          results.push(user._id)
        } catch (err: unknown) {
          if (getErrorStatus(err) === 401) {
            navigate("/login")
            return
          }
          errors.push({
            userId: user._id,
            userName: user.name,
            error: getErrorMessage(err),
          })
        }
      }

      if (results.length > 0) {
        setSuccess(`${results.length} kullanıcı için başvuru başarıyla oluşturuldu`)
        if (errors.length > 0) {
          setError(
            `${errors.length} kullanıcı için hata oluştu: ${errors
              .map((e) => e.userName)
              .join(", ")}`,
          )
        }
      } else {
        setError("Hiçbir başvuru oluşturulamadı")
      }
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      } else {
        setError(getErrorMessage(err) || "Başvurular oluşturulurken bir hata oluştu")
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Seçilen Kullanıcıları Davet Et</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-4 mb-4">
          {/* Campaign ID Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kampanya _id *</label>
            <input
              type="text"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              placeholder="Kampanya _id girin..."
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Campaign Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kampanya Tipi *</label>
            <select
              value={campaignType}
              onChange={(e) => setCampaignType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              {CAMPAIGN_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Bulk Price Update */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tümü için Kullanıcı Tutarı
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0"
                onChange={(e) =>
                  handleBulkPriceChange("price_user", parseFloat(e.target.value) || 0)
                }
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tümü için Marka Tutarı
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0"
                onChange={(e) =>
                  handleBulkPriceChange("price_brand", parseFloat(e.target.value) || 0)
                }
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Users List with Prices */}
          <div className="border border-gray-300 rounded-md overflow-hidden">
            <div className="bg-gray-100 p-2 font-semibold grid grid-cols-4 gap-4">
              <div>Kullanıcı Adı</div>
              <div>Kullanıcı Tutarı *</div>
              <div>Marka Tutarı *</div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {selectedUsers.map((user) => {
                const userPrice = userPrices.find((up) => up.userId === user._id)
                return (
                  <div
                    key={user._id}
                    className="p-2 border-b border-gray-200 grid grid-cols-4 gap-4 items-center hover:bg-gray-50"
                  >
                    <div className="font-medium">{user.name || user.insta?.username || "N/A"}</div>
                    <div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={userPrice?.price_user || 0}
                        onChange={(e) =>
                          handlePriceChange(user._id, "price_user", parseFloat(e.target.value) || 0)
                        }
                        className="w-full p-1 border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={userPrice?.price_brand || 0}
                        onChange={(e) =>
                          handlePriceChange(
                            user._id,
                            "price_brand",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        className="w-full p-1 border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {error && <div className="text-red-500 mb-4">{error}</div>}
        {success && <div className="text-green-500 mb-4">{success}</div>}

        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
          >
            İptal
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !campaignId || campaignId.trim() === ""}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? "Oluşturuluyor..." : "Başvuruları Oluştur"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default InviteUsersModal
